import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Plus, Pencil, Trash2, Copy, Calculator, FileSpreadsheet, Upload, Download } from "lucide-react";
import ListinoEditor from "@/components/ListinoEditor";
import SimulatoreDialog from "@/components/SimulatoreDialog";
import ImportListiniDialog from "@/components/ImportListiniDialog";

export default function Listini() {
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState("tutti");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const dl = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = u;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/listini").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/listini/config").then((r) => setConfig(r.data));
    load();
  }, []);

  const duplica = async (l) => {
    try {
      await api.post(`/listini/${l.id}/duplica`, {});
      toast.success("Listino duplicato");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/listini/${toDelete.id}`);
      toast.success("Listino eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const visible = items.filter((l) =>
    (tipo === "tutti" || l.soggetto_tipo === tipo) &&
    (!search || `${l.nome} ${l.soggetto_nome}`.toLowerCase().includes(search.toLowerCase()))
  );
  const canWrite = config?.can_write;

  return (
    <div className="space-y-4" data-testid="listini-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Input className="h-9 w-64" placeholder="Cerca listino o soggetto..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="listini-search" />
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="h-9 w-48" data-testid="listini-filtro-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i soggetti</SelectItem>
            <SelectItem value="cliente">Clienti (attivi)</SelectItem>
            <SelectItem value="subvettore">Sub-vettori (passivi)</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-9" onClick={() => dl("/listini-excel/modello", "modello_listini.xlsx")} data-testid="listini-modello">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" />Modello Excel
          </Button>
          {canWrite && (
            <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="listini-importa">
              <Upload className="h-4 w-4 mr-1.5" />Importa da Excel
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={() => dl("/listini-excel/export", "listini.xlsx")} disabled={items.length === 0} data-testid="listini-export-all">
            <Download className="h-4 w-4 mr-1.5" />Esporta tutti
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setSimOpen(true)} data-testid="listini-simula">
            <Calculator className="h-4 w-4 mr-1.5" />Simulatore tariffa
          </Button>
          {canWrite && (
            <Button size="sm" className="h-9" onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="listini-nuovo">
              <Plus className="h-4 w-4 mr-1.5" />Nuovo listino
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="listini-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Listino</th>
              <th className="text-left px-3 py-2 font-semibold">Soggetto</th>
              <th className="text-left px-3 py-2 font-semibold">Tipo</th>
              <th className="text-left px-3 py-2 font-semibold">Validità</th>
              <th className="text-right px-3 py-2 font-semibold">Tariffe</th>
              <th className="text-right px-3 py-2 font-semibold">Gasolio</th>
              <th className="text-left px-3 py-2 font-semibold">Supplementi</th>
              <th className="text-left px-3 py-2 font-semibold">Stato</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Caricamento...</td></tr>}
            {!loading && visible.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Nessun listino. Crea il primo listino per un cliente o sub-vettore.</td></tr>}
            {visible.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`listino-row-${l.id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{l.nome}</td>
                <td className="px-3 py-2">{l.soggetto_nome || "—"}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={l.soggetto_tipo === "cliente" ? "border-sky-300 text-sky-700" : "border-amber-300 text-amber-700"}>{l.soggetto_tipo === "cliente" ? "Cliente" : "Sub-vettore"}</Badge></td>
                <td className="px-3 py-2 text-xs text-slate-600">{l.valido_dal || l.valido_al ? `${formatDate(l.valido_dal)} → ${l.valido_al ? formatDate(l.valido_al) : "∞"}` : "Sempre"}</td>
                <td className="px-3 py-2 text-right">{l.n_tariffe}</td>
                <td className="px-3 py-2 text-right">{l.gasolio_periodi?.length ? `${l.gasolio_periodi.length} periodi` : `${l.gasolio_pct || 0}%`}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(l.supplementi || []).map((s) => s.codice).join(", ") || "—"}</td>
                <td className="px-3 py-2">{l.attivo === false ? <Badge variant="outline" className="text-slate-500">Disattivo</Badge> : <Badge variant="outline" className="badge-verde">Attivo</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(l); setEditorOpen(true); }} data-testid={`listino-edit-${l.id}`} title={canWrite ? "Modifica" : "Visualizza"}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dl(`/listini-excel/export?listino_id=${l.id}`, `listino_${l.nome.replace(/\W+/g, "_")}.xlsx`)} title="Esporta Excel" data-testid={`listino-export-${l.id}`}><Download className="h-4 w-4" /></Button>
                  {canWrite && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplica(l)} title="Duplica" data-testid={`listino-dup-${l.id}`}><Copy className="h-4 w-4" /></Button>}
                  {canWrite && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setToDelete(l)} title="Elimina" data-testid={`listino-del-${l.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {config && <ListinoEditor open={editorOpen} onOpenChange={setEditorOpen} config={config} initial={editing} onSaved={load} />}
      {config && <SimulatoreDialog open={simOpen} onOpenChange={setSimOpen} config={config} listini={items} />}
      <ImportListiniDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il listino "{toDelete?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>Le prefatture già generate non vengono modificate.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="listino-confirm-delete">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
